import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

/**
 * MagicTrail — a single-shot particle streak that arcs from the ball's
 * center to the target board cell.
 *
 * Props:
 *   value             — the number that was revealed
 *   sourceSelector    — CSS selector for the ball center element
 *                        (default: `[data-testid="projector-ball-anchor"]`)
 *   targetSelector    — CSS selector for the target cell
 *                        (default: `[data-testid="projector-cell-{value}"]`)
 *   color             — theme color for the trail (matches the ball)
 *   onDone            — called when the trail animation finishes
 *   durationMs        — total animation duration (default 400ms)
 */
export default function MagicTrail({
  value,
  sourceSelector = '[data-testid="projector-ball-anchor"]',
  targetSelector,
  color = "#F59E0B",
  onDone,
  durationMs = 400,
}) {
  const [endpoints, setEndpoints] = useState(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const source = document.querySelector(sourceSelector);
      const target = document.querySelector(
        targetSelector || `[data-testid="projector-cell-${value}"]`,
      );
      if (!source || !target) {
        onDone?.();
        return;
      }
      const s = source.getBoundingClientRect();
      const t = target.getBoundingClientRect();
      setEndpoints({
        x1: s.left + s.width / 2,
        y1: s.top + s.height / 2,
        x2: t.left + t.width / 2,
        y2: t.top + t.height / 2,
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [value, sourceSelector, targetSelector, onDone]);

  // Schedule unmount when the animation is over.
  useEffect(() => {
    if (!endpoints) return;
    const id = window.setTimeout(() => onDone?.(), durationMs + 80);
    return () => window.clearTimeout(id);
  }, [endpoints, durationMs, onDone]);

  const path = useMemo(() => {
    if (!endpoints) return null;
    const { x1, y1, x2, y2 } = endpoints;
    // Convex arc: control point pulled upward relative to the midpoint
    // so the trail rises then dives into the board — reads like a comet.
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.hypot(dx, dy);
    // Perpendicular offset (upward-biased) for the control point.
    const norm = { x: -dy / dist, y: dx / dist };
    const bias = Math.min(220, dist * 0.35);
    const cX = midX + norm.x * bias;
    const cY = midY + norm.y * bias - 40; // small extra upward lift
    return `M ${x1} ${y1} Q ${cX} ${cY} ${x2} ${y2}`;
  }, [endpoints]);

  if (!endpoints || !path) return null;

  const trailId = `trail-${value}`;
  const dur = `${durationMs / 1000}s`;

  return (
    <svg
      data-testid={`projector-magic-trail-${value}`}
      aria-hidden
      className="fixed inset-0 pointer-events-none z-50"
      width="100%"
      height="100%"
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient id={`${trailId}-grad`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0" />
          <stop offset="30%" stopColor={color} stopOpacity="0.6" />
          <stop offset="90%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
        </linearGradient>
        <filter id={`${trailId}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* The trail path itself — animated via stroke-dashoffset */}
      <motion.path
        d={path}
        fill="none"
        stroke={`url(#${trailId}-grad)`}
        strokeWidth={5}
        strokeLinecap="round"
        filter={`url(#${trailId}-glow)`}
        initial={{ pathLength: 0, opacity: 0.9 }}
        animate={{ pathLength: 1, opacity: [0.9, 1, 0] }}
        transition={{
          pathLength: { duration: durationMs / 1000, ease: [0.4, 0.0, 0.2, 1] },
          opacity: {
            duration: durationMs / 1000,
            times: [0, 0.6, 1],
            ease: "easeOut",
          },
        }}
      />

      {/* Bright leading head that rides along the same path */}
      <motion.circle
        r={9}
        fill="#ffffff"
        filter={`url(#${trailId}-glow)`}
        initial={{ opacity: 1 }}
        animate={{ opacity: [1, 1, 0] }}
        transition={{
          duration: durationMs / 1000,
          ease: "easeOut",
          times: [0, 0.85, 1],
        }}
      >
        <animateMotion
          dur={dur}
          fill="freeze"
          keyTimes="0;1"
          keySplines="0.4 0 0.2 1"
          calcMode="spline"
          path={path}
        />
      </motion.circle>
    </svg>
  );
}
